import moment from 'moment';
import React, { useState } from 'react';
import CsvDownloader from 'react-csv-downloader';

import { EBool } from '~/src/dash/api/types/bool';
import { ISimpleLineItemRaw } from '~/src/dash/apiV2/campaigns';
import { IStartToEndDateRanges } from '~/src/dash/components/calendar/common';
import {
  ECampaignDeliveryStatus,
  ECampaignStatusToSticker,
  EDebugPrebidData,
} from '~/src/dash/components/filters/options/campaigns';
import { DownloadIcon } from '~/src/dash/components/icons/misc-icons';
import { Col, Cols, ct, spacing, TooltipWithTranslation } from '~/src/dash/publicaUI';
import { addCommasToIntegerOrFloat } from '~/src/dash/utils/basic';
import { publicaColors } from '~/src/dash/utils/colors';

export interface ICSVColumn {
  id: string;
  displayName: string;
}

interface IProps {
  data: () => Promise<ILineItemCSVData[]>;
  filename: string;
  isDisabled: boolean;
  title: string;
}

export interface ILineItemCSVData {
  Active: boolean;
  ID: number;
  Name: string;
  OrderID: number;
  OrderName: string;
  Status: string;
  StartDate: string; // YYYY-MM-DD
  EndDate: string; // YYYY-MM-DD
  AdvertiserName: string;
  CPM: string;
  Impressions: string;
  Goal: string;
  Pacing: string;
  Revenue: string;
  UpdatedAt: string;
}

const activeEnumToText = (enumVal: EBool): string => {
  if (enumVal === EBool.EBool_TRUE) {
    return ct('Active');
  } else if (enumVal === EBool.EBool_FALSE) {
    return ct('Inactive');
  }
  return ct('ShowAll');
};

const debugEnumToText = (enumVal: EDebugPrebidData): string => {
  if (enumVal === EDebugPrebidData.SiteWeightEnabled) {
    return ct('SiteWeightEnabled');
  } else if (enumVal === EDebugPrebidData.NotLowPacing) {
    return ct('NotLowPacing');
  }
  return ct('ShowAll');
};

export const generateLineItemsCSVTitle = (
  filterParams: Record<string, string>,
  dateRangeFilter: IStartToEndDateRanges,
): string => {
  const paramsString: string[] = Object.entries(filterParams || {})
    .filter(([_, value]) => value?.length !== 0)
    .map(([key, value]) => {
      let valueString = value;
      if (key === 'active') {
        const enumVal = Number(value) as EBool;
        valueString = activeEnumToText(enumVal);
      } else if (key === 'status') {
        const statuses: string[] = [];
        for (const estatus of value) {
          const enumVal = Number(estatus) as ECampaignDeliveryStatus;
          const { tKey } = ECampaignStatusToSticker(enumVal);
          statuses.push(ct(tKey));
        }
        valueString = statuses.join(', ');
      } else if (key === 'debug') {
        const enumVal = Number(value) as EDebugPrebidData;
        valueString = debugEnumToText(enumVal);
      }
      return `${key}: ${valueString}`;
    });
  if (dateRangeFilter.startDateFrom) {
    paramsString.push(`start_date_from: ${dateRangeFilter.startDateFrom.format('YYYY-MM-DD')}`);
  }
  if (dateRangeFilter.startDateTo) {
    paramsString.push(`start_date_to: ${dateRangeFilter.startDateTo.format('YYYY-MM-DD')}`);
  }
  if (dateRangeFilter.endDateFrom) {
    paramsString.push(`end_date_from: ${dateRangeFilter.endDateFrom.format('YYYY-MM-DD')}`);
  }
  if (dateRangeFilter.endDateTo) {
    paramsString.push(`end_date_to: ${dateRangeFilter.endDateTo.format('YYYY-MM-DD')}`);
  }
  return paramsString.join('\n');
};

export const formatDateTimeUnchangedString = (dateTime: string): string => {
  let res = '';
  if (dateTime !== '') {
    // Parse the timestamp and preserve the original timezone offset
    const parsedMoment = moment.parseZone(dateTime);
    res = parsedMoment.format('YYYY-MM-DD HH:mm');
  }
  return res;
};

export const formatMultiCurrencyString = (value: number, currencySymbol = '$'): string => {
  return `${currencySymbol} ${addCommasToIntegerOrFloat(value.toFixed(2))}`;
};

export const formatLineItemsDataToCSV = (
  data: ISimpleLineItemRaw[],
): Promise<ILineItemCSVData[]> => {
  const formattedData: ILineItemCSVData[] = [];
  data.forEach((item) => {
    const { tKey } = ECampaignStatusToSticker(item.Status);
    const rowData: ILineItemCSVData = {
      Active: item.Active === EBool.EBool_TRUE,
      ID: item.ID,
      Name: item.Name,
      OrderID: item.OrderID,
      OrderName: item.OrderName,
      Status: ct(tKey),
      StartDate: formatDateTimeUnchangedString(item.StartDate),
      EndDate: formatDateTimeUnchangedString(item.EndDate),
      AdvertiserName: item.AdvertiserName,
      CPM: formatMultiCurrencyString(item.CPM, item.CurrencySymbol),
      Impressions: addCommasToIntegerOrFloat(item.DeliveredImps.toString()),
      Goal: addCommasToIntegerOrFloat(item.Goal.toString()),
      Pacing: `${item.Pacing} %`,
      Revenue: formatMultiCurrencyString(item.Revenue, item.CurrencySymbol),
      UpdatedAt: formatDateTimeUnchangedString(item.UpdatedAt),
    };
    formattedData.push(rowData);
  });
  return Promise.resolve(formattedData);
};

export const ExportLineItemsCSVButton = ({
  data,
  filename,
  isDisabled,
  title,
}: IProps): React.ReactNode => {
  const [isHovered, setIsHovered] = useState(false);
  const color = isHovered ? publicaColors.veryDarkGray : publicaColors.gray;
  const csvColumns = [
    { id: 'Active', displayName: ct('Active') },
    { id: 'ID', displayName: ct('ID') },
    { id: 'Name', displayName: ct('Name') },
    { id: 'OrderID', displayName: ct('OrderID') },
    { id: 'OrderName', displayName: ct('Order') },
    { id: 'Status', displayName: ct('Status') },
    { id: 'StartDate', displayName: ct('StartDate') },
    { id: 'EndDate', displayName: ct('EndDate') },
    { id: 'AdvertiserName', displayName: ct('Advertiser') },
    { id: 'CPM', displayName: ct('NetCPM') },
    { id: 'Impressions', displayName: ct('DeliveredImpressions') },
    { id: 'Goal', displayName: ct('Goal') },
    { id: 'Pacing', displayName: ct('Pacing') },
    { id: 'Revenue', displayName: ct('Revenue') },
    { id: 'UpdatedAt', displayName: ct('LastUpdated') },
  ];

  return (
    <CsvDownloader
      columns={csvColumns}
      datas={data}
      filename={filename}
      extension='.csv'
      separator=';'
      disabled={isDisabled}
      title={title}
      newLineAtEnd
      meta={true}
      bom={true}
    >
      <TooltipWithTranslation tKey='DownloadCSV'>
        <div
          onMouseEnter={() => !isDisabled && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Cols
            gap={spacing.small}
            style={{
              alignItems: 'center',
              height: '30px',
              padding: '0 8px',
              borderRadius: '6px',
              border: `1px solid ${publicaColors.lightGray}`,
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              color,
              opacity: isDisabled ? '0.4' : '1',
            }}
          >
            <Col displayFlex>
              <DownloadIcon color={color} style={{ height: '12px' }} />
            </Col>
          </Cols>
        </div>
      </TooltipWithTranslation>
    </CsvDownloader>
  );
};
